import { GsFormField, GsFormFieldColor, GsFormFieldPadding, GsFormIf } from 'libs/shared/src/components';
import { GsStructBorderRadius, GsStructBorderStyle, GsStructBorderWidth } from '../../../models';
import { borderSchemaFields } from './border.schema';
import { colorBackgroundGeneratorSchema, colorTextGeneratorSchema } from './colorSchema.schema';

/** Shared translation root for all button schema labels */
const T = 'schema.buttons';

/** Sub-group translation root */
const G = `${T}.group`;

/**
 * Generates all button-related form schema fields with shared translation keys.
 * Use this in any schema that needs button styling fields (UI-Elements, Checkout, ShoppingCart, etc.).
 *
 * @param groupingName - Optional groupingName for field grouping. Omit when fields are inside a 'group' field
 *                        (sub-groups like "Hintergrund", "Text", "Rahmen", "Abstand" are used instead).
 * @returns GsFormField array with all button styling fields
 */
export function buttonSchemaFields(groupingName?: string): GsFormField<never>[] {
  /** When no outer groupingName is given (nested in a group), use sub-groups */
  const bg = groupingName ?? `${G}.background`;
  const text = groupingName ?? `${G}.text`;
  const border = groupingName ?? `${G}.border`;
  const spacing = groupingName ?? `${G}.spacing`;

  /** Condition: borderStyle is not 'none' — hides irrelevant border fields */
  const hasBorderStyleIf: GsFormIf = {
    args: [{ formControlName: 'borderStyle', value: GsStructBorderStyle.none, operator: '!=' }]
  };

  return [
    // Background colors
    ...colorBackgroundGeneratorSchema(undefined, `${T}.background`, 'secondary-main', undefined, bg),
    ...colorBackgroundGeneratorSchema('hover', `${T}.hoverBackground`, 'secondary-shade', undefined, bg),
    ...colorBackgroundGeneratorSchema('disabled', `${T}.disabledBackground`, 'medium-shine', undefined, bg),

    // Text colors
    ...colorTextGeneratorSchema(undefined, `${T}.text`, 'secondary-contrast', undefined, text),
    ...colorTextGeneratorSchema('hover', `${T}.hoverText`, 'secondary-contrast', undefined, text),
    ...colorTextGeneratorSchema('disabled', `${T}.disabledText`, 'medium-contrast', undefined, text),

    // Font
    {
      formControlName: 'font',
      type: 'font',
      label: `${T}.font`,
      default: { family: 'source-sans-3', weight: 400, style: 'normal', textTransform: 'none' },
      groupingName: text
    },

    // Text size
    {
      formControlName: 'textSize',
      type: 'number',
      label: `${T}.textSize`,
      isResponsive: true,
      default: { default: 16 },
      groupingName: text
    },

    // Text caps
    {
      formControlName: 'textCaps',
      label: `${T}.textCaps`,
      type: 'checkbox',
      default: false,
      groupingName: text
    },

    // Border settings (width, style, color, radius)
    ...borderSchemaFields(
      undefined,
      {
        borderWidth: GsStructBorderWidth.zeroPixel,
        borderStyle: GsStructBorderStyle.none,
        borderColor: 'secondary-main',
        borderRadius: GsStructBorderRadius.none
      },
      border,
      undefined,
      `${T}.border`
    ),

    // Border hover color
    {
      formControlName: 'borderHoverColor',
      label: `${T}.borderHoverColor`,
      type: 'color',
      default: 'secondary-shade',
      if: hasBorderStyleIf,
      groupingName: border
    } as GsFormFieldColor,

    // Border disabled color
    {
      formControlName: 'borderDisabledColor',
      label: `${T}.borderDisabledColor`,
      type: 'color',
      default: 'medium-shine',
      if: hasBorderStyleIf,
      groupingName: border
    } as GsFormFieldColor,

    // Inner padding
    {
      formControlName: 'innerPadding',
      label: `${T}.innerPadding`,
      type: 'padding',
      isResponsive: true,
      default: { default: { top: 8, right: 24, bottom: 8, left: 24 } },
      groupingName: spacing
    } as GsFormFieldPadding
  ];
}
